import { useEffect } from "react";
import { isNoindexFollowPath } from "@shared/static-routes";

const SITE_NAME = "AdVenture Media";
const SITE_URL = "https://adventuremedia.ai";
const DEFAULT_IMAGE = `${SITE_URL}/images/og/og-default.png`;
const TWITTER_HANDLE = "@adventureppc";

/**
 * Per-series title / description / social card for workshop detail pages.
 *
 * MUST mirror SERIES_SEO + SERIES_OG_IMAGE in server/page-meta.ts. The server
 * writes the head into the SSR HTML and `usePageMeta` rewrites it on
 * hydration, so any drift between the two shows up as a title that changes a
 * second after the page loads (it did: the served title ended "| AdVenture
 * Media Workshops" and the hydrated one "| AdVenture AI Workshops").
 *
 * Keyed by series slug. A series with no entry keeps the generic
 * `${event.title} | AdVenture AI Workshops` fallback.
 */
export const WORKSHOP_SERIES_SEO: Record<
  string,
  { title: string; description: string; image?: string }
> = {
  "claude-for-beginners-workshop": {
    title: "Claude for Beginners Workshop | AdVenture AI",
    description:
      "Master Claude in 3 live hours with Isaac Rudansky. The Claude training we run for teams at Chase, ESPN, and T-Mobile, now open to everyone. Beginners welcome.",
  },
  "claude-for-sales-teams-workshop": {
    title: "The Claude for Sales Teams Workshop | AdVenture AI",
    description:
      "Build a Claude system for prospect research, outreach, call prep, and follow-up on your own pipeline. A live half day with Brigid Healy and Isaac Rudansky.",
    image: "/images/og/og-claude-for-sales-teams-workshop.png",
  },
  "ai-video-workshop-for-beginners": {
    title: "AI Video Workshop for Beginners | AdVenture AI",
    description:
      "Make AI images and video in a live, four-hour workshop with Webby-nominated director Dave Spector, and leave with a first draft of your own commercial.",
  },
  "ai-security-workshop": {
    title: "AI Privacy & Security Masterclass | AdVenture AI",
    description:
      "Learn what AI tools can see, what can be faked and what you can control. A live two-hour, plain-language session with Kate Marshall, author of AI at Work.",
  },
};

interface PageMeta {
  title: string;
  description: string;
  path: string;
  /** Optional override for canonical URL + og:url. When omitted, falls back to
   *  `path`. Used by event detail pages to consolidate every session URL in a
   *  series down to one canonical (`/events/<series_slug>`) so search engines
   *  see one indexable landing page per series instead of one per dated session. */
  canonicalPath?: string;
  image?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  jsonLd?: Record<string, any> | Record<string, any>[];
}

export function usePageMeta({
  title,
  description,
  path,
  canonicalPath,
  image,
  type = "website",
  noindex = false,
  jsonLd,
}: PageMeta) {
  useEffect(() => {
    const alreadyHasSiteName = title.includes(SITE_NAME) || title.includes("AdVenture");
    const fullTitle = alreadyHasSiteName ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = `${SITE_URL}${canonicalPath ?? path}`;
    // Accept either an absolute URL (https://…) or a site-relative path ("/images/…").
    // Social scrapers require absolute URLs, so normalize relatives to absolute.
    const ogImage = image
      ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
      : DEFAULT_IMAGE;

    document.title = fullTitle;

    const metaTags: Record<string, string> = {
      description,
      "og:title": fullTitle,
      "og:description": description,
      "og:type": type,
      "og:url": canonicalUrl,
      "og:image": ogImage,
      "og:site_name": SITE_NAME,
      "twitter:card": "summary_large_image",
      "twitter:site": TWITTER_HANDLE,
      "twitter:title": fullTitle,
      "twitter:description": description,
      "twitter:image": ogImage,
    };

    if (isNoindexFollowPath(path)) {
      // Workshop slide decks / notes: out of the index but links still
      // followed, matching the SSR robots tag from server/page-meta.ts.
      metaTags["robots"] = "noindex, follow";
    } else if (noindex) {
      metaTags["robots"] = "noindex, nofollow";
    } else {
      metaTags["robots"] = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
    }

    const createdElements: HTMLElement[] = [];

    Object.entries(metaTags).forEach(([key, value]) => {
      const isOg = key.startsWith("og:");
      const isTwitter = key.startsWith("twitter:");
      const attr = isOg || isTwitter ? "property" : "name";
      const attrName = isOg ? "property" : "name";

      let el = document.querySelector(`meta[${attrName}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        createdElements.push(el);
      }
      el.content = value;
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // If the SSR layer already injected this page's JSON-LD (marked via
    // data-ssr-jsonld in server/page-meta.ts → applyJsonLd), skip the
    // client-side injection. Otherwise GSC sees both server- and client-
    // emitted schemas in the live DOM and reports "Duplicate field FAQPage"
    // (and similar dup-entity errors for Event/BreadcrumbList).
    const hasSsrJsonLd =
      document.querySelector('script[type="application/ld+json"][data-ssr-jsonld]') !== null;

    let jsonLdScript = document.getElementById("page-jsonld") as HTMLScriptElement | null;
    if (jsonLd && !hasSsrJsonLd) {
      if (!jsonLdScript) {
        jsonLdScript = document.createElement("script");
        jsonLdScript.id = "page-jsonld";
        jsonLdScript.type = "application/ld+json";
        document.head.appendChild(jsonLdScript);
      }
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      jsonLdScript.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
    } else if (jsonLdScript) {
      jsonLdScript.remove();
    }

    return () => {
      createdElements.forEach((el) => el.remove());
      if (createdCanonical && canonical) canonical.remove();
    };
  }, [title, description, path, canonicalPath, image, type, noindex]);
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "ProfessionalService"],
    "@id": `${SITE_URL}/#organization`,
    name: "AdVenture Media Group",
    // Keep in sync with server/page-meta.ts organizationSchema — the two
    // definitions had drifted (GSC sees both when the client copy renders).
    alternateName: "AdVenture Media",
    url: SITE_URL,
    logo: `${SITE_URL}/images/adventure-logo.svg`,
    description: "AI-powered digital advertising agency specializing in performance media, creative production, and marketing automation.",
    foundingDate: "2013",
    founder: {
      "@type": "Person",
      name: "Isaac Rudansky",
    },
    sameAs: [
      "https://www.linkedin.com/company/adventureppc/",
      "https://twitter.com/adventureppc",
      "https://www.facebook.com/adventureppc",
      "https://www.instagram.com/adventureppc/",
      "https://www.youtube.com/@adventuremedia",
      "https://clutch.co/profile/adventure-media-group",
      // Real first-party reviews live on these profiles — pointing here is the
      // compliant alternative to self-marked aggregateRating (removed 2026-06).
      "https://www.google.com/search?q=adventure+media+group#lrd=0x89c265c02837d0bd:0x79cafd562781151,1,,,,",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "1412 Broadway, 21st Floor",
      addressLocality: "New York",
      addressRegion: "NY",
      postalCode: "10018",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      url: `${SITE_URL}/contact`,
    },
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    publisher: {
      "@type": "Organization",
      name: "AdVenture Media Group",
    },
    // SearchAction removed: the sitelinks search box was retired by Google
    // (Nov 2024) and the target pointed at /blog?q= which isn't a search
    // endpoint anyway.
  };
}

export function serviceSchema(name: string, description: string, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_URL}${url}`,
    provider: {
      "@type": "Organization",
      name: "AdVenture Media Group",
      url: SITE_URL,
    },
    areaServed: "Worldwide",
    serviceType: "Digital Marketing",
    // No aggregateRating: Google's review-snippet policy makes entities that
    // control their own reviews ineligible for star markup, and ratings must
    // be sourced directly from users (not aggregated from other sites). Real
    // reviews live on the Google + Clutch profiles linked in Organization
    // sameAs; on-page testimonials remain as visible content only.
  };
}

export function faqSchema(items: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "AdVenture Media Group",
    image: `${SITE_URL}/images/adventure-logo.svg`,
    url: SITE_URL,
    telephone: "+1-212-714-9255",
    priceRange: "$$$$",
    address: [
      {
        "@type": "PostalAddress",
        streetAddress: "1412 Broadway, 21st Floor",
        addressLocality: "New York",
        addressRegion: "NY",
        postalCode: "10018",
        addressCountry: "US",
      },
      {
        "@type": "PostalAddress",
        streetAddress: "433 Plaza Real, Suite 275",
        addressLocality: "Boca Raton",
        addressRegion: "FL",
        postalCode: "33432",
        addressCountry: "US",
      },
      {
        "@type": "PostalAddress",
        addressLocality: "London",
        addressCountry: "GB",
      },
    ],
    // No aggregateRating: a LocalBusiness self-marking its own reviews is
    // ineligible under Google's review-snippet policy. Real reviews are
    // surfaced via the Google + Clutch sameAs links instead.
  };
}

export const PAGE_META: Record<string, { title: string; description: string; image?: string }> = {
  "/taylor-swift-effect-impact-on-nfl-super-bowl-lviii": {
    title: "Taylor Swift Effect on NFL — Super Bowl LVIII Earned Media Report",
    description: "AdVenture Media tracked Taylor Swift's earned media value to the NFL during Super Bowl LVIII in real-time: 711K+ posts, 1B+ impressions, $9.6M estimated value.",
    image: "/images/og/og-default.png",
  },
  "/testimonials": {
    title: "Client Testimonials — A Decade of Reviews | AdVenture Media",
    description: "A decade of client testimonials and reviews. What partners, marketers, and founders say about working with AdVenture Media's performance and AI teams.",
    image: "/images/og/og-default.png",
  },
  "/measurement": {
    title: "Data & Measurement — Server-Side Tracking, MMM, Attribution",
    description: "Server-side conversion tracking, incrementality testing, media-mix modeling, and full-funnel attribution for performance media. Measurement built to defend every dollar.",
    image: "/images/og/og-default.png",
  },
  "/": {
    title: "AdVenture Media — Performance Marketing & AI Transformation Agency",
    description: "Performance marketing and AI transformation agency. Google Ads, Meta Ads, LinkedIn, and full-funnel lead generation powered by proprietary AI. Founded in 2013.",
    image: "/images/og/og-home.png",
  },
  "/services": {
    title: "Marketing Services — AI-Powered Paid Media, SEO, Creative",
    description: "Full-service AI-native marketing agency: Google Ads, Meta Ads, LinkedIn, SEO, creative production, measurement, and consulting. 500+ clients, $1.5B+ managed.",
  },
  // Service pages (root-level Webflow slugs)
  "/google-ads-management": {
    title: "Google Ads Management Agency — AdVenture Media",
    description: "Google Ads management services from a Google Premier Partner. AI-powered Search, Performance Max, Shopping, and YouTube campaigns. 500+ clients, $100M+ managed.",
    image: "/images/og/og-google-ads-management.png",
  },
  "/meta-ads-management": {
    title: "Meta Ads Management Agency — AdVenture Media",
    description: "Expert Meta Ads management across Facebook, Instagram, and the Audience Network. AI-optimized targeting, creative testing, and bidding strategies.",
    image: "/images/og/og-meta-ads-management.png",
  },
  "/landing-page-design": {
    title: "Landing Page Design — AdVenture Media",
    description: "Conversion-optimized landing page design and development. Brand-true, measurable, and ready to ship for paid traffic and lead generation.",
    image: "/images/og/og-landing-page-design.png",
  },
  "/landing-page-ab-testing": {
    title: "Landing Page A/B Testing — AdVenture Media",
    description: "Data-driven A/B testing for landing pages. Increase conversions with AI-powered multivariate testing and rapid experimentation.",
    image: "/images/og/og-landing-page-ab-testing.png",
  },
  "/google-shopping-management": {
    title: "Google Shopping Management Agency — AdVenture Media",
    description: "Product feed optimization and Google Shopping campaign management for ecommerce brands. Maximize ROAS with AI-powered bid management.",
    image: "/images/og/og-google-shopping-management.png",
  },
  "/display-ads": {
    title: "Display Advertising Agency — AdVenture Media",
    description: "Strategic display advertising across Google Display Network and programmatic platforms. AI-optimized targeting, creative, and bidding.",
    image: "/images/og/og-display-ads.png",
  },
  "/retargeting": {
    title: "Retargeting Agency — AdVenture Media",
    description: "Advanced retargeting and remarketing strategies across Google, Meta, and programmatic platforms. Re-engage your highest-value audiences.",
    image: "/images/og/og-retargeting.png",
  },
  "/creative-services": {
    title: "Creative Services — AdVenture Media",
    description: "AI-native creative production: video ads, static assets, ad copy, and landing pages. Production-ready creative at scale.",
    image: "/images/og/og-creative-services.png",
  },
  "/seo-agency-services": {
    title: "SEO Agency Services — AdVenture Media",
    description: "AI-powered SEO services including technical SEO, content strategy, programmatic page generation, and link building.",
    image: "/images/og/og-seo-agency-services.png",
  },
  "/answer-engine-optimization-aeo-services": {
    title: "Answer Engine Optimization (AEO) — AdVenture Media",
    description: "Optimize your brand's presence in AI-powered search engines like ChatGPT, Perplexity, and Google AI Overviews.",
    image: "/images/og/og-answer-engine-optimization-aeo-services.png",
  },
  "/linkedin-ads-agency": {
    title: "LinkedIn Ads Agency — AdVenture Media",
    description: "Expert LinkedIn advertising management for B2B brands. Precision targeting, lead gen forms, and account-based marketing.",
    image: "/images/og/og-linkedin-ads-agency.png",
  },
  "/pinterest-ads-agency": {
    title: "Pinterest Ads Agency — AdVenture Media",
    description: "Pinterest advertising management for ecommerce and lifestyle brands. Shopping ads, idea pins, and visual search optimization.",
    image: "/images/og/og-pinterest-ads-agency.png",
  },
  "/programmatic-advertising-agency": {
    title: "Programmatic Advertising Agency — AdVenture Media",
    description: "Full-service programmatic advertising agency. AI-powered bidding and audience targeting across display, video, native, CTV, and audio on premium DSP inventory.",
    image: "/images/og/og-programmatic-advertising-agency.png",
  },
  "/google-local-service-ads": {
    title: "Google Local Service Ads Management — AdVenture Media",
    description: "Google Local Service Ads management for home services, legal, medical, and local businesses. Pay-per-lead advertising with Google Guaranteed.",
    image: "/images/og/og-google-local-service-ads.png",
  },
  "/pay-per-click-agency": {
    title: "PPC Agency — AdVenture Media",
    description: "Full-service pay-per-click management across Google, Bing, Meta, and more. AI-powered bidding, targeting, and creative optimization.",
    image: "/images/og/og-pay-per-click-agency.png",
  },
  "/digital-advertising-consulting": {
    title: "Digital Advertising Consulting — AdVenture Media",
    description: "Expert digital advertising consulting for brands and agencies. Strategy audits, media planning, and performance optimization.",
  },
  "/lead-gen-services": {
    title: "Lead Generation Services — AdVenture Media",
    description: "AI-powered lead generation across Google, Meta, LinkedIn, and beyond. Call tracking, CRM integration, and Sherpa-powered optimization that cuts CPA and improves lead quality.",
    image: "/images/og/og-lead-gen-services.png",
  },
  "/ai-transformation-services": {
    title: "AI Transformation Services — AdVenture Media",
    description: "Enterprise AI onboarding, custom software builds, and ongoing advisory. We come into your company and build the AI infrastructure your team will actually use.",
    image: "/images/og/og-ai-transformation-services.png",
  },
  "/in-house-training": {
    title: "In-House Training — AdVenture Media",
    description: "Custom in-house training for marketing teams. Google Ads, Meta Ads, AI tools, measurement, and strategy, delivered by senior practitioners.",
    image: "/images/og/og-in-house-training.png",
  },
  "/ai-training-for-teams": {
    title: "AI Training for Teams — AdVenture Media",
    description: "Private AI workshops shaped to your team's stack, tools, and projects. Custom curriculum, live cohort sessions, and 30-day follow-up. Delivered by the same instructors who teach our public cohorts.",
    image: "/images/og/og-team-training.png",
  },
  "/nyf": {
    title: "Build Your Agency’s AI Agent | NYF × AdVenture Live Workshop",
    description: "A live, three-hour workshop from AdVenture and New York Festivals on Nov 18. Build an AI agent for your agency’s busywork and keep the creative work.",
  },
  // Industry pages
  "/home-service-lead-generation": {
    title: "Home Service Lead Generation — AdVenture Media",
    description: "Digital advertising for home service companies. HVAC, plumbing, roofing, and more. Google Local Service Ads, Search, and lead generation.",
    image: "/images/og/og-home-service-lead-generation.png",
  },
  "/industries/saas": {
    title: "SaaS Marketing Agency — AdVenture Media",
    description: "AI-powered digital advertising for SaaS companies. Demand generation, trial acquisition, and expansion revenue campaigns.",
  },
  "/industries/automotive": {
    title: "Automotive Marketing Agency — AdVenture Media",
    description: "AI-powered digital advertising for automotive dealerships, service centers, and auto brands. Google Ads, GLSA, Vehicle Listing Ads, Meta Ads, Local SEO, and multi-channel lead generation that drives calls, appointments, and drive-ups.",
  },
  "/industries/fitness-sporting-goods": {
    title: "Fitness & Sporting Goods Marketing — AdVenture Media",
    description: "Digital advertising for fitness brands and sporting goods retailers. Ecommerce, DTC, and omnichannel marketing.",
  },
  "/consulting/audit": {
    title: "AI Architecture Audit",
    description: "Comprehensive assessment of your marketing AI readiness with a custom implementation roadmap.",
  },
  "/consulting/ai": {
    title: "AI Consulting",
    description: "Hands-on AI integration into your marketing workflows. Strategy, implementation, and training.",
  },
  "/training/dolah": {
    title: "DOLAH Conference",
    description: "The premier conference for AI-powered marketing. Workshops, speakers, and networking.",
  },
  "/training/claude-design-brief-to-creative/kit": {
    title: "Your kit: Brief in, creative out · Mirren AI Forum | AdVenture Media",
    description: "The take-home kit from Esther Nadoff's Mirren AI Forum talk: every step and prompt to take a client brief to a moodboard, a shoot, a landing page, ads, a film and an email in Claude.",
  },
  "/training/claude-design-brief-to-creative": {
    title: "Design with Claude: Brief In, Creative Out — Mirren AI Forum | AdVenture Media",
    description: "Esther Nadoff's live demo deck: one client brief taken to concepts, visual direction, ads, a storyboard and a landing page in Claude.",
  },
  "/training/claude-for-design-workshop-slides": {
    title: "The interactive Claude Design Workshop — Slides | AdVenture Media",
    description: "The Claude for Design Workshop deck: set up a client in Claude, lock in the design system, then take one brief to a moodboard and a landing page, hands on.",
  },
  "/training/claude-for-design-workshop-kit": {
    title: "Your kit: The interactive Claude Design Workshop | AdVenture Media",
    description: "The take-home kit from the Claude for Design Workshop: every step and prompt to set up your brand in Claude, lock in a design system, and build a moodboard, a landing page and more.",
  },
  "/training/claude-code-workshop-slides-v2": {
    title: "Claude Code for Beginners (Vivid) — Workshop Slides | AdVenture Media",
    description: "Interactive slide deck for the Claude Code workshop, Vivid edition. Install, first build, CLAUDE.md, a data dashboard, and deploying live.",
  },
  "/training/claude-code-workshop-slides": {
    title: "Claude Code for Beginners — Workshop Slides | AdVenture Media",
    description: "Interactive slide deck for the Claude Code workshop. Installation, AI crash course, group projects, planning, essential commands, and deployment.",
  },
  "/training/claude-cowork-workshop-slides": {
    title: "Claude Cowork for Beginners — Workshop Slides | AdVenture Media",
    description: "Interactive slide deck for the Claude Cowork workshop. Autonomous tasks, connectors, plugins, scheduled automations, and building real documents with Claude Cowork.",
  },
  "/training/claude-cowork-workshop-slides/print": {
    title: "Claude Cowork for Beginners — Printable Workshop Slides | AdVenture Media",
    description: "Print-friendly version of the Claude Cowork workshop slide deck.",
  },
  "/training/claimfox-cowork-workshop-slides": {
    title: "Claude Cowork for ClaimFox — Private Workshop Slides | AdVenture Media",
    description: "Private, password-protected slide deck for the ClaimFox Claude Cowork workshop.",
  },
  "/training/claude-for-sales-workshop-slides": {
    title: "Claude for Sales — Workshop Slides | AdVenture Media",
    description: "Password-protected slide deck for the Claude for Sales live workshop.",
  },
  "/training/medisca-training-slides": {
    title: "Claude for Medisca — Private Training Slides | AdVenture Media",
    description: "Private, password-protected slide deck for the Medisca full-day Claude training.",
  },
  "/training/mobilefuse-sales-slides": {
    title: "Claude for Sales, MobileFuse — Private Session Slides | AdVenture Media",
    description: "Private, password-protected slide deck for the MobileFuse Claude for Sales 1:1 session.",
  },
  "/training/claude-dataviz-workshop-slides": {
    title: "Data Visualization with Claude Code — Workshop Slides | AdVenture Media",
    description: "Interactive slide deck for the Data Visualization with Claude Code workshop. Connectors, skills, the data-viz vocabulary that upgrades every chart, and a copyable prompt kit for real dashboards.",
  },
  "/sherpa": {
    title: "Sherpa — AI-Powered Campaign Intelligence | AdVenture Media",
    description: "Sherpa is our proprietary AI engine for cross-channel attribution, automated reporting, and campaign intelligence — built for performance marketing teams.",
  },
  "/clients": {
    title: "Our Clients — 500+ Brands We've Driven Results For",
    description: "Brands we partner with across e-commerce, SaaS, lead generation, healthcare, and home services. Real client roster, real testimonials, real measurable performance.",
    image: "/images/og/og-clients.png",
  },
  "/ecommerce": {
    title: "Ecommerce Marketing Agency — AdVenture Media",
    description: "AI-powered ecommerce marketing agency. Google Shopping, Performance Max, Meta DPA, Amazon Ads, and full-funnel paid media strategy built for online retail brands.",
  },
  "/books": {
    title: "Our Books",
    description: "Books on marketing, advertising, and AI by the AdVenture Media team.",
  },
  "/reading-list": {
    title: "Reading List",
    description: "Curated reading recommendations from the AdVenture team on marketing, AI, and business.",
  },
  "/tools": {
    title: "Tools & Calculators",
    description: "Free marketing tools and calculators. Budget planners, ROAS calculators, and more.",
  },
  "/blog": {
    title: "Blog — AI Marketing Insights",
    description: "Expert insights on AI in advertising, performance marketing, creative automation, and digital strategy from the AdVenture Media team.",
  },
  "/case-studies": {
    title: "Case Studies — Proven AI Marketing Results",
    description: "Real results from real clients. See how AdVenture Media drives measurable ROI with AI-powered advertising, creative production, and marketing automation.",
    image: "/images/og/og-case-studies.png",
  },
  "/about-us": {
    title: "About AdVenture Media — AI-Native Performance Marketing Agency Since 2013",
    description: "Founded 2013. 500+ clients. $1.5B+ managed ad spend. The strategists, engineers, and creatives building the future of AI-powered performance marketing.",
    image: "/images/og/og-about-us.png",
  },
  "/membership": {
    title: "AdVenture+ | Ongoing AI Training: Live Sessions, Tools and 50% Off Workshops",
    description:
      // Mirrors server/page-meta.ts. No price or date: search snippets outlive the offer.
      "The membership for ongoing AI training. Two live sessions a month where we build, demo and explain what changed in AI, two Claude courses, the Agent Context Kit and 50% off every workshop.",
    image: "/images/og/og-default.png",
  },
  "/membership/first-month": {
    title: "AdVenture+ October Recap: 4 Live AI Sessions, 4 Real Builds",
    description:
      "What AdVenture+ members watched in October 2026: an AI agent built live in under an hour, 3 AIs on one project, the instructions your AI reads, and video made with Claude. One session free to watch in full.",
    image: "/images/og/og-default.png",
  },
  "/ai-context-toolkit": {
    title: "Free AI Context Toolkit for Claude | AdVenture Media",
    description:
      "Free toolkit. Answer a few questions in 7 minutes and get the files Claude reads before every chat: what you sell, who you sell to and how you talk.",
    image: "/images/og/og-default.png",
  },
  "/dashboard": {
    title: "Member Dashboard | AdVenture Media",
    description: "Your workshops, slide decks, and recordings in one place.",
  },
  "/login": {
    title: "Sign In | AdVenture Media",
    description: "Sign in to your AdVenture member dashboard.",
  },
  "/signup": {
    title: "Create Account | AdVenture Media",
    description: "Create your AdVenture member account.",
  },
  "/design-builder": {
    title: "Claude Design Builder | AdVenture Media",
    description: "Build your own design system with Claude, one approved step at a time.",
  },
  "/claim": {
    title: "Claim Your Access | AdVenture Media",
    description: "Claim your workshop member access.",
  },
  "/contact": {
    title: "Get a Proposal — Contact AdVenture Media",
    description: "Ready to transform your marketing with AI? Get a free proposal from AdVenture Media. We'll audit your current strategy and build a custom roadmap.",
    image: "/images/og/og-contact.png",
  },
  "/ai-adoption-assessment": {
    title: "AI Adoption Assessment for Teams | AdVenture Media",
    description:
      "An eighteen-question assessment that shows where every person on your team sits on the AI adoption curve, before we build your training. Usage, knowledge, attitude, access, and workflow fit.",
    image: "/images/og/og-ai-readiness.png",
  },
  "/ai-readiness": {
    title: "AI Readiness Assessment",
    description: "Evaluate your organization's AI readiness with our comprehensive assessment. Get a personalized roadmap for integrating AI into your marketing stack.",
    image: "/images/og/og-ai-readiness.png",
  },
  "/claude-cowork-recipe-generator": {
    title: "Claude Cowork Recipe Generator | AdVenture Media",
    description: "Answer 6 short questions. A live Claude agent researches your company and writes 3 personalized Cowork recipes you can build today. Free, 90 seconds.",
    image: "/images/og/og-cowork-recipe-generator.png",
  },
  "/terms": {
    title: "Terms of Service",
    description: "Terms of Service for AdVenture Media and AdVenture Media Group digital properties and services.",
  },
  "/claude-code-for-kids/program-terms": {
    title: "Program Terms: Claude Code for Kids",
    description:
      "The plain-English program terms for Claude Code for Kids: parent supervision, session recordings, refunds, and children's privacy.",
  },
  "/kids/tonight": {
    title: "Tonight's prompt — Claude Code Camp for Kids",
    description:
      "The Session 1 prompt for campers to copy, plus what to do if the Code tab will not open.",
  },
  "/training/kids-camp/session-1": {
    title: "Session 1 — Claude Code Camp for Kids",
    description:
      "Camper slide deck for Session 1: how large language models actually work, tokens, next-word prediction, the anatomy of a good prompt, and the first build.",
  },
  "/training/kids-parent-orientation-slides": {
    title: "Parent Orientation — Claude Code Camp for Kids",
    description:
      "Slide deck for the Claude Code Camp for Kids parent orientation: the six-night road map, tech setup, how to coach without taking over, and safety.",
  },
  "/claude-code-for-kids/build-profile": {
    title: "Camper Intake: Claude Code Camp for Kids",
    description:
      "Enrolled families only. Tell us what your camper loves so we have real project ideas waiting for them on night one.",
  },
  "/claude-code-for-kids/demo-night": {
    title: "Demo Night RSVP: Claude Code Camp for Kids",
    description:
      "Enrolled families only. Tell us who's coming to Family Demo Night on September 2 so we can build the demo schedule.",
  },
  "/claude-code-for-kids/showcase": {
    title: "Showcase Permission: Claude Code Camp for Kids",
    description:
      "Enrolled families only. Tell us whether we may feature your camper's project on the AdVenture website. First name only, always.",
  },
  "/claude-code-for-kids/review": {
    title: "Leave a Review: Claude Code Camp for Kids",
    description:
      "Camp families only. Share your experience of Claude Code Camp for Kids on Google, with starter text you can edit into your own words.",
  },
  "/privacy": {
    title: "Privacy Policy",
    description: "Privacy Policy for AdVenture Media. Learn how we collect, use, and protect your personal information.",
  },
  "/workshops": {
    title: "AI Workshops, Webinars & Training — Live Hands-On Cohorts | AdVenture Media",
    description: "Live, instructor-led online webinars and hands-on AI workshops. Six tracks: Claude Cowork, Claude Code, Advanced Claude Code, Claude for Design, Claude for Sales, and The AI-First Leader.",
  },
  // Legacy alias — kept so any code still keying off "/events" gets sensible
  // meta. Cloudflare 301s the URL to /workshops at the edge.
  "/events": {
    title: "AI Workshops, Webinars & Training — Live Hands-On Cohorts | AdVenture Media",
    description: "Live, instructor-led online webinars and hands-on AI workshops. Six tracks: Claude Cowork, Claude Code, Advanced Claude Code, Claude for Design, Claude for Sales, and The AI-First Leader.",
  },
  // Lab: the three.js Impressionist Room. noindex, nofollow (ALWAYS_NOINDEX_PREFIXES
  // in server/page-meta.ts); routable but out of the sitemap.
  "/lab/museum": {
    title: "The Impressionist Rooms",
    description:
      "Ten paintings, three connected rooms, and a small museum for the curious. Explore the story of the 1874 Impressionist exhibition.",
  },
  "/careers": {
    title: "Careers at AdVenture Media — Build the Future of AI Marketing",
    description: "Join AdVenture Media. 30+ strategists, engineers, and creatives across New York, Philadelphia, and Fort Lauderdale building the future of AI-powered performance marketing.",
  },
};
